import React from 'react';
import { Shield, Lock, Eye, Server, Users, Heart, Code2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import DownloadSourceButton from '../components/DownloadSourceButton';

const About = () => {
  const values = [
    {
      icon: Lock,
      title: 'Privacy First',
      description: 'We believe privacy is a fundamental right, not a feature. Every decision we make prioritizes your data security.',
    },
    {
      icon: Eye,
      title: 'Transparency',
      description: 'Our code is open source and auditable. We have nothing to hide because we collect nothing.',
    },
    {
      icon: Server,
      title: 'Zero Storage',
      description: 'We cannot access your data because it never leaves your device. This is by design, not promise.',
    },
    {
      icon: Users,
      title: 'User Empowerment',
      description: 'We build tools that give you control over your digital security without requiring technical expertise.',
    },
  ];

  return (
    <div className="py-20 animate-fade-in">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            About <span className="gradient-text">NoTrackTools</span>
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            We are building the future of privacy-first security tools.
          </p>
        </div>

        {/* Mission Section */}
        <div className="glass-card p-8 lg:p-12 mb-16 max-w-4xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <Shield className="w-12 h-12 text-primary" />
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-center mb-6">
            Our Mission
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed text-center">
            In an era where privacy is constantly under threat, we created NoTrackTools to provide
            professional-grade security tools that respect your fundamental right to privacy.
            Our platform operates on a simple principle:
            <span className="block mt-4 text-xl font-semibold text-foreground">
              Your data is yours alone.
            </span>
          </p>
        </div>

        {/* Values Grid */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our <span className="gradient-text">Core Values</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={index}
                  className="glass-card p-6 hover:scale-105 transition-smooth"
                >
                  <div className="flex items-start space-x-4">
                    <div className="p-3 glass rounded-lg">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* How We're Different */}
        <div className="glass-card p-8 lg:p-12 mb-16 bg-muted/30">
          <h2 className="text-2xl lg:text-3xl font-bold text-center mb-8">
            How We're <span className="gradient-text">Different</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-lg font-semibold mb-3 text-destructive">Other Tools</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Upload your data to their servers
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Track your usage and behavior
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Store your files and history
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Require account registration
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3 text-success">NoTrackTools</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  Everything runs in your browser
                </li>
                <li className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  Zero tracking or analytics
                </li>
                <li className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  No data storage whatsoever
                </li>
                <li className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  No registration required
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Technology */}
        <div className="text-center glass-card p-8 lg:p-12 max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl lg:text-3xl font-bold mb-6">
            Built on <span className="gradient-text">Modern Web Standards</span>
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Our tools leverage cutting-edge browser APIs including Web Crypto API for encryption,
            Canvas API for image processing, and File API for local file handling. Everything is
            client-side JavaScript with zero server dependencies.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {['Web Crypto API', 'Canvas API', 'File API', 'LocalStorage', 'IndexedDB'].map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 glass rounded-full text-sm font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
          
          {/* Download Source Code Section */}
          <div className="glass p-6 rounded-lg border border-primary/20 bg-primary/5">
            <Code2 className="w-10 h-10 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-3">Open Source & Transparent</h3>
            <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
              We believe in full transparency. Download our complete source code and verify
              that we practice what we preach about privacy.
            </p>
            <DownloadSourceButton 
              variant="default" 
              size="lg" 
              className="hover:glow-primary"
            />
            <p className="text-xs text-muted-foreground mt-3">
              Includes frontend React code, backend API, and configuration files.<br />
              Sensitive data like API keys are excluded for security.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center glass-card p-8 lg:p-12 max-w-2xl mx-auto hero-pattern">
          <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Join the Privacy Movement</h2>
          <p className="text-muted-foreground mb-6">
            Start using our tools today and take control of your digital privacy.
          </p>
          <Link to="/explore">
            <Button size="lg" className="hover:glow-primary">
              Explore Tools
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
